import styled from 'styled-components'
import { Wave } from '../../../components/wave/Wave'
import { theme } from '../../../theme/theme'
import { BodyMedium, BodyXlMedium } from '../../../components/styles/Typography'
import {
  useGetUserFollowUpQueryContent,
  useGetUserScreeningStatisticsQuery,
} from '../../../api/ester-b2b-api/private/users/userQueries'
import { MarkdownToReact } from '../../../components/markdown/MardownToReact'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isMobile } from '../../../utils/devices'
import { ErrorGuard } from '../../../components/ErrorGuard'
import { FollowUpLayout } from '../../../components/layouts/FollowUpLayout'
import { Stack } from '@mui/material'
import { SingleLineChart } from '../../../components/charts/SingleLineChart'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

export const YourWellbeing = () => {
  const { t } = useTranslation()
  const {
    data: followUpContent,
    isLoading: isFollowUpContentLoading,
    isError: isFollowUpContentError,
    error: followUpContentError,
  } = useGetUserFollowUpQueryContent()
  const { data: screeningStatisticsData } = useGetUserScreeningStatisticsQuery()
  const { generalWellbeing } = screeningStatisticsData || {}

  const width = useWindowWidth()
  const isMobileDevice = isMobile(width)

  const chartData = generalWellbeing
    ? generalWellbeing.map(({ date, value }) => ({
        name: format(new Date(date || new Date()), 'MMMM yyyy', { locale: sv }),
        nameMobile: format(new Date(date || new Date()), 'MMM yy', { locale: sv }),
        value,
      }))
    : []

  return (
    <>
      {!isMobileDevice && (
        <StyledWaveWrapper>
          <Wave color={theme.color.plum} />
        </StyledWaveWrapper>
      )}
      <FollowUpLayout
        backgroundColor={theme.color.plum}
        paddingTop={isMobileDevice ? theme.spacing.xxlarge : theme.spacing.small}
      >
        <FollowUpWrapper>
          {isFollowUpContentLoading ? (
            <LoadingSpinner color={theme.color.white} size={60} />
          ) : (
            <FollowUpContainer>
              <Stack gap={isMobileDevice ? 5 : 10}>
                {followUpContent && (
                  <Stack gap={2}>
                    <StyledBodyXlMedium>{t('employee.yourHealth.summary')}</StyledBodyXlMedium>
                    <Container>
                      {isFollowUpContentError ? (
                        <ErrorGuard error={followUpContentError} />
                      ) : (
                        <MarkdownToReact markdown={followUpContent} />
                      )}
                    </Container>
                  </Stack>
                )}

                <Stack gap={2}>
                  <StyledBodyXlMedium>{t('employee.yourHealth.trackMoodTitle')}</StyledBodyXlMedium>
                  <StyledBodyMedium>{t('employee.yourHealth.trackMoodDescription')}</StyledBodyMedium>

                  <SingleLineChart
                    isMobileDevice={isMobileDevice}
                    data={chartData}
                    title={t('employee.yourHealth.generalMood')}
                    color={theme.color.green}
                  />

                  <StyledBodyMedium>{t('employee.yourHealth.moreStatsText')}</StyledBodyMedium>
                </Stack>
              </Stack>
            </FollowUpContainer>
          )}
        </FollowUpWrapper>
      </FollowUpLayout>
    </>
  )
}

const FollowUpContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 748px;
  width: 100%;
`

const FollowUpWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: calc(100vh - 355px);
`
const StyledWaveWrapper = styled.div`
  z-index: 0;
  margin-top: -${theme.spacing.xxlarge}px;
`
const StyledBodyXlMedium = styled(BodyXlMedium)`
  color: ${theme.color.white};
`

const Container = styled.div`
  background-color: ${theme.color.white};
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  padding: ${theme.spacing.xlarge}px ${theme.spacing.mediumLarge}px;
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.medium}px ${theme.spacing.small}px;
  }
`

const StyledBodyMedium = styled(BodyMedium)`
  color: ${theme.color.white};
`
